import { Injectable, inject } from "@angular/core";
import { Router, NavigationExtras } from "@angular/router";
import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";

import { SignUpEntryService } from "./entry.service";
import { SignUpRedirectModalService } from "./redirect.service";
import { SignupAnalyticsService } from "./analytics.service";
import { SignUpHubspotService } from "./hubspot.service";

import { PageSizeModalComponent } from "../signupPageSizeModal/signup-pagesize-modal.component";
import { SignupCheckVerificationService } from "@onsip/common/services/api/resources/SignupCheckVerificationService/signup-check-verification.service";
import { Config } from "@onsip/common/config";
export enum Step {
  SIGNUP = "signup",
  SIGNUP_DOMAIN = "signup-domain",
  SIGNUP_PROFILE = "signup-profile",
  SIGNUP_TOPICS = "signup-topics",
  SIGNUP_ADD_USERS = "signup-add-users",
  SIGNUP_CMS = "signup-cms",
  SIGNUP_INSTALLATION = "signup-installation"
}

const debug = true;

@Injectable({ providedIn: "root" })
export class SignUpNavigationService {
  private router = inject(Router);
  private redirect = inject(SignUpRedirectModalService);
  private signupCheckVerificationService = inject(SignupCheckVerificationService);
  private hubspot = inject(SignUpHubspotService);
  private entry = inject(SignUpEntryService);
  private analytics = inject(SignupAnalyticsService);
  private breakpoint = inject(BreakpointObserver);
  private dialog = inject(MatDialog);

  isMobile = false;
  xSmall = false;
  isBeta = Config.ADMIN_API_URL.includes("beta");
  private currentStep: string = Step.SIGNUP_DOMAIN;
  private dialogRef!: MatDialogRef<PageSizeModalComponent>;

  constructor() {
    this.init();
  }

  navigateForward(currentStep: Step, extras?: NavigationExtras): void {
    debug && console.log("SignUpNavigationService.navigateForward", currentStep, extras);

    switch (currentStep) {
      case Step.SIGNUP_DOMAIN:
        this.router.navigate([`signup/${Step.SIGNUP_PROFILE}`]);
        this.analytics.sendAnalyticsEvent(Step.SIGNUP_DOMAIN, false);
        this.currentStep = Step.SIGNUP_PROFILE;
        break;
      case Step.SIGNUP_PROFILE:
        this.redirect.signupComplete();
        this.analytics.sendAnalyticsEvent(Step.SIGNUP_PROFILE, true);
        break;
      // case Step.SIGNUP_ADD_USERS:
      //   this.router.navigate([`signup/${Step.SIGNUP_CMS}`]);
      //   this.analytics.sendAnalyticsEvent(Step.SIGNUP_ADD_USERS);
      //   break;
    }
  }

  navigateBackward(): void {
    const currentStep = this.router.parseUrl(this.router.url).root.children.primary.segments[1]
      .path;
    this.currentStep = currentStep;
    debug && console.log("SignUpNavigationService.navigateBackward", currentStep);

    switch (currentStep) {
      case Step.SIGNUP_DOMAIN:
        break;
      case Step.SIGNUP_PROFILE:
        this.router.navigate([`signup/${Step.SIGNUP_DOMAIN}`]);
        this.currentStep = Step.SIGNUP_DOMAIN;
        break;
      // case Step.SIGNUP_ADD_USERS:
      //   this.router.navigate([`signup/${Step.SIGNUP_TOPICS}`]);
      //   break;
    }
  }

  validateStepName(step: string): Step {
    const valid = (str: string): str is Step =>
      str === Step.SIGNUP ||
      str === Step.SIGNUP_DOMAIN ||
      str === Step.SIGNUP_PROFILE ||
      str === Step.SIGNUP_ADD_USERS;
    if (valid(step)) return step;
    else throw Error("SignupNavigationService: Attempted an invalid step.");
  }

  handleSuccessGuardRoute(): void {
    this.router.navigate(["/" + Step.SIGNUP], { skipLocationChange: true });
  }

  handleFailedGuardRoute(): void {
    // handle mobile route if user tries to navigate pass the blocking modal
    // or do nothing if it is mobile
    if (!this.isMobile) {
      this.router.navigate([`signup/${Step.SIGNUP_TOPICS}`], { skipLocationChange: true });
      this.currentStep = Step.SIGNUP_TOPICS;
      this.pageSizeError();
    }
  }

  private init(): void {
    this.isMobile = navigator.userAgent.toLowerCase().indexOf("mobile") !== -1;
    this.breakpoint.observe(Breakpoints.XSmall).subscribe(res => {
      this.xSmall = res.matches;
      if (!this.xSmall && this.dialogRef) this.dialogRef.close();
      if (this.xSmall) this.pageSizeError();
    });

    // Do not check for verification token if signup is from automated testing
    if (this.entry.automatedTesting || this.isBeta) return;

    if (this.entry.verificationToken) {
      this.signupCheckVerificationService
        .signupCheckVerification(this.entry.verificationToken)
        .then(
          () => {
            this.analytics.sendAnalyticsEvent(Step.SIGNUP, false);
            if (this.entry.email) {
              this.hubspot.hubspotInitRequest({
                email: this.entry.email,
                company: this.entry.orgName || "",
                numemployees: this.entry.orgSize || "",
                leadsource: this.entry.leadSource || "",
                lead_type: this.entry.leadType || "",
                contact_phone: this.entry.contactPhone || "",
                name: this.entry.name || ""
              });
            }
          },
          () => {
            this.redirect.redirectToApp();
          }
        );
    } else if (
      (!this.entry.verificationToken && !this.entry.cookie) ||
      (this.entry.cookie &&
        (!this.entry.oauthToken || !this.entry.oAuthService || !this.entry.oAuthServiceUserId))
    ) {
      this.redirect.redirectToApp();
    } else {
      this.analytics.sendAnalyticsEvent(Step.SIGNUP, false);
    }
  }

  private pageSizeError(): void {
    if (!this.isMobile && this.xSmall && this.currentStep === Step.SIGNUP_TOPICS) {
      this.dialog.closeAll();
      this.dialogRef = this.dialog.open(PageSizeModalComponent, {
        width: "395px",
        disableClose: true,
        panelClass: ["mat-typography", "onsip-dialog-universal-style"]
      });
    }
  }
}
